import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, from, switchMap, throwError } from 'rxjs';
import { AuthService } from 'vlife-common';

const AUTH_ENDPOINTS = ['auth/login', 'auth/refresh'];

function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINTS.some((path) => url.includes(path));
}

// Guards the rotating refresh token against concurrent 401s racing each other, both
// within this tab (parallel requests) and across tabs (navigator.locks is shared per origin).
async function refreshAccessToken(auth: AuthService, staleAccessToken: string | null): Promise<string> {
  return navigator.locks.request('vl-auth-refresh', async () => {
    const currentAccessToken = auth.getAccessToken();
    if (currentAccessToken && currentAccessToken !== staleAccessToken) {
      return currentAccessToken;
    }

    const refreshToken = auth.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const result = await firstValueFrom(auth.refresh(refreshToken));
    auth.storeTokens(result.access_token, result.refresh_token);
    return result.access_token;
  });
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const auth = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 403) {
        router.navigate(['/forbidden']);
        return throwError(() => error);
      }

      if (error.status !== 401 || isAuthEndpoint(req.url)) {
        return throwError(() => error);
      }

      const hardLogout = () => {
        auth.clearTokens();
        router.navigate(['/login']);
      };

      const refreshToken = auth.getRefreshToken();
      if (!refreshToken) {
        hardLogout();
        return throwError(() => error);
      }

      const staleAccessToken = auth.getAccessToken();
      return from(refreshAccessToken(auth, staleAccessToken)).pipe(
        switchMap((newAccessToken) => {
          const retriedReq = req.clone({ setHeaders: { Authorization: `Bearer ${newAccessToken}` } });
          return next(retriedReq);
        }),
        catchError(() => {
          hardLogout();
          return throwError(() => error);
        }),
      );
    }),
  );
};
